import { Image } from '../../../../modules/design-system';
import type { TierBadge } from '../../../../modules/api';
import { formatPrice } from '../../../../modules/currency';

type Props = {
  badge: TierBadge;
  onClick?: () => void;
};

/**
 * One sibling pack in the glass panel (repackz-handoff §8.1): name and price
 * on top, the pack art rising from the bottom edge and cut by the card. The
 * picked one is rimmed. Same `--co-TierCard-*` knobs as the classic card.
 */
export function TierCard({ badge, onClick }: Props) {
  const { name, price, imageUrl, isSelf } = badge;
  const rim = isSelf
    ? {
        borderWidth: 'var(--co-TierCard-activeBorderWidth, 1px)',
        borderStyle: 'solid' as const,
        borderColor: 'var(--co-TierCard-activeBorder, #ffffff)',
      }
    : undefined;

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={isSelf}
      data-co-component="TierCard"
      className="relative h-[115px] w-[calc((100%-16px)/3)] min-w-[120px] shrink-0 cursor-pointer overflow-hidden transition-[filter] duration-200 hover:brightness-110"
      style={{
        borderRadius: 'var(--co-TierCard-borderRadius, 24px)',
        background:
          'linear-gradient(var(--co-TierCard-gradientDirection, to bottom), ' +
          'var(--co-TierCard-gradientFrom, rgba(255,255,255,0.11)), ' +
          'var(--co-TierCard-gradientTo, rgba(255,255,255,0.11)))',
        ...rim,
      }}
    >
      <span className="absolute inset-x-2 top-[11px] flex flex-col items-center text-[13px] leading-[19px]">
        <span className="max-w-full truncate font-medium" style={{ color: 'var(--co-TierCard-titleText, #ffffff)' }}>
          {name}
        </span>
        <span className="font-bold" style={{ color: 'var(--co-TierCard-priceText, #ffffff)' }}>
          {formatPrice(Number(price) || 0)}
        </span>
      </span>
      <Image
        src={imageUrl}
        alt=""
        className="pointer-events-none absolute left-1/2 top-[68px] h-[141px] w-[113px] -translate-x-1/2 [filter:drop-shadow(0_16px_50px_rgba(0,0,0,0.45))]"
        imgClassName="absolute inset-0 h-full w-full object-contain object-top"
      />
    </button>
  );
}
